import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ShieldCheck } from "lucide-react";
import { useAuth } from "../../contexts/AuthContext";

/**
 * The platform administration shell — deliberately NOT the customer Layout.
 *
 * It carries no organization switcher, no notification bell and no tenant
 * navigation, and it never reads the selected customer organization. A platform
 * administrator does not have to belong to any organization at all.
 */
export const PlatformShell = ({ title, actions, children }) => {
  const { t } = useTranslation();
  const { me, signOut, orgs, orgsLoaded } = useAuth();
  const nav = useNavigate();
  // Phase 2D.1B fix — a standalone platform admin (no tenant organization) has
  // no "app" to go back to; /dashboard would just bounce them into tenant
  // onboarding via <Guarded>. Hide the link rather than send it somewhere
  // broken. Kept hidden while orgs are still loading to avoid a flash.
  const hasTenantOrg = orgsLoaded && (orgs?.length || 0) > 0;

  return (
    <div className="qw-platform" data-testid="platform-shell">
      <header className="qw-platform-top">
        <div className="qw-platform-brand">
          <ShieldCheck size={18} strokeWidth={2} aria-hidden="true" />
          <span className="qw-platform-brand-name">{t("platform.title")}</span>
        </div>
        <div className="qw-platform-top-actions">
          {hasTenantOrg && (
            <Link to="/dashboard" className="qw-btn ghost sm" data-testid="platform-back-to-app">
              {t("platform.back_to_app")}
            </Link>
          )}
          <button
            type="button"
            className="qw-btn ghost sm"
            onClick={async () => {
              await signOut();
              nav("/login");
            }}
            data-testid="platform-sign-out"
          >
            {t("auth.sign_out")}
          </button>
        </div>
      </header>
      <main className="qw-platform-body">
        <div className="qw-platform-head">
          <h1 className="h1" data-testid="platform-page-title">{title}</h1>
          {actions && <div className="hstack">{actions}</div>}
        </div>
        {me?.email && (
          <div className="qw-help" data-testid="platform-signed-in-as">
            {t("platform.signed_in_as", { name: me.full_name || me.email })}
          </div>
        )}
        <div style={{ height: 16 }} />
        {children}
      </main>
    </div>
  );
};

export default PlatformShell;
